// Inbox › Messages (gap #3): every text and email with the company's customers, newest first, by
// status (new replies and the ones that couldn't be sent first to find), channel and customer; one
// message's detail with what happened to it; Send again for a failed one (and its copy), a reply
// opened is read (and can be marked unread), a reply from a number on no customer is said to be an
// existing customer's (messages.assignCustomer) or becomes a new one, and a new message or a reply to
// a customer. The stream keeps the list and its counts current.
// BL-189: the list on ListPage under the Inbox's Calls and Texts and email tabs (PageHead's), the
// statuses a Segmented with their counts, the channel a ListPick, a message a RowList row. What's open
// (a message, New message, Whose text is this?, New customer) is its own page: PageHead with the crumb
// back to the messages over its Sections, on the same frame; New customer NewCustomerFields.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import {
  Button,
  Choice,
  ChoiceGroup,
  KeyValue,
  Label,
  NewCustomerFields,
  Link,
  RowList,
  Segmented,
  TextField,
  type KeyValueItem,
  type LabelKind,
  type Row,
  type RowTone,
} from '@flowology/ui-web/fl';
import { ListPage, ListPick, Section, type Crumb, type ListRegion } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { liveStream } from '../connection/live.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { useNewCustomer } from '../shared/use-new-customer.ts';
import { formFromSearch } from '../shared/new-customer-model.ts';
import {
  MESSAGE_EVENTS,
  visibleFilters,
  STATUS_FILTERS,
  bodyText,
  channelLine,
  errorWords,
  firstLine,
  isFailed,
  isReply,
  isUnread,
  mergeFirstPage,
  messageTime,
  momentText,
  purposeWords,
  replyWords,
  statusChip,
  theirAddress,
  whenText,
  type Message,
  type StatusFilter,
} from './messages-model.ts';
import type { Customer } from '../types.ts';
import { draftTitle, SendMessage } from './SendMessage.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { talkTabs } from '../inbox/TalkTabs.tsx';
import './messages-page.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

interface Page {
  messages: Message[];
  next: string | null;
}

/** Where Messages opens from elsewhere: one job's texts and emails (a job sheet, QA-391), or a customer's (their history). */
export type MessagesStart =
  { job: { id: string; number: string } } | { customer: { id: string; name: string } };

const CHANNELS = [
  { value: '', label: 'Texts and email' },
  { value: 'sms', label: 'Texts' },
  { value: 'email', label: 'Email' },
] as const;

/** A message's word, as its row and its page say it: red for a problem, dashed while it waits. */
const rowTone = (tone: ReturnType<typeof statusChip>['tone']): RowTone =>
  tone === 'bad' ? 'bad' : tone === 'warn' ? 'wait' : tone === 'brand' ? 'ink' : 'muted';
const labelKind = (tone: ReturnType<typeof statusChip>['tone']): LabelKind =>
  tone === 'bad' ? 'bad' : tone === 'warn' ? 'wait' : 'muted';

/** One message in the list: who, when beside them, what it was and what it said; how it went at the end. */
export function messageRow(m: Message, timeZone: string, onOpen: (m: Message) => void): Row {
  const chip = statusChip(m);
  const what = (isReply(m) ? replyWords(m) : purposeWords(m.purpose)).toLowerCase();
  return {
    id: m.id,
    name: m.customerName ?? theirAddress(m),
    onOpen: () => onOpen(m),
    after: { text: ` · ${whenText(messageTime(m), timeZone)}`, size: 'sm' },
    // a reply no one has opened: its word and its line in ink
    lines: [
      {
        text: `${m.channel === 'sms' ? 'Text' : 'Email'}, ${what}: “${firstLine(m.subject ?? bodyText(m))}”`,
        ...(isUnread(m) ? { tone: 'ink' as const, size: 'sm' as const } : {}),
      },
    ],
    end: { word: chip.text, tone: rowTone(chip.tone) },
  };
}

export function MessagesScreen({
  nav,
  timeZone,
  canSend,
  start,
}: {
  nav: NavProps;
  timeZone: string;
  /** messages.send: write to a customer and send a failed message again. */
  canSend: boolean;
  start?: MessagesStart | null;
}) {
  // While the company mirrors ServiceTitan nothing goes to a customer from here (st-mirror §3: sends
  // are refused), and a new customer is ServiceTitan's; whose text it is stays ours to say (BL-088)
  const mirroring = useMirror();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [channel, setChannel] = useState<'' | 'sms' | 'email'>('');
  const [who, setWho] = useState<{ id: string; name: string } | null>(
    start && 'customer' in start ? start.customer : null,
  );
  const [job, setJob] = useState<{ id: string; number: string } | null>(
    start && 'job' in start ? start.job : null,
  );
  const [list, setList] = useState<Message[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [failedCount, setFailedCount] = useState<number | null>(null);
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [heldCount, setHeldCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [writing, setWriting] = useState<{ customerId: string; to?: string } | 'pick' | null>(null);
  // a reply from a number on no customer: "Say whose it is" (an existing customer) or "Add as a customer"
  const [assigning, setAssigning] = useState<Message | null>(null);
  const [adding, setAdding] = useState<Message | null>(null);
  // a message opened that isn't in the list shown (a failed one's copy, under another filter)
  const [extra, setExtra] = useState<Message | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [texting, setTexting] = useState<{ ok: boolean; notSetUp: string } | null>(null);
  // a job sheet or a customer's history opened it again while it's showing: theirs, from the top
  useEffect(() => {
    if (!start) return;
    setJob('job' in start ? start.job : null);
    setWho('customer' in start ? start.customer : null);
    setStatus('all');
    setOpenId(null);
  }, [start]);
  useEffect(() => {
    if (!canSend) return;
    client
      .call<{ sms: boolean; notSetUp: string }>('messages.channels', {})
      .then((r) => setTexting({ ok: r.sms, notSetUp: r.notSetUp }))
      .catch(() => setTexting(null));
  }, [canSend]);

  const query = useCallback(
    (before?: string) => {
      const f: { direction?: 'in' | 'out'; status?: readonly string[] } =
        STATUS_FILTERS.find((x) => x.key === status)?.query ?? {};
      return client.call<Page>('messages.list', {
        limit: 50,
        ...(f.status ? { status: [...f.status] } : {}),
        ...(f.direction ? { direction: f.direction } : {}),
        ...(channel ? { channel } : {}),
        ...(who ? { customerId: who.id } : {}),
        ...(job ? { jobId: job.id } : {}),
        ...(before ? { before } : {}),
      });
    },
    [status, channel, who, job],
  );
  // An answer to a filter no longer showing is dropped (QA-555): a refresh in flight when the filter
  // changed would land the old filter's rows in the new list, and they'd stay.
  const current = useRef(query);
  current.current = query;
  useEffect(() => {
    let stale = false;
    setList(null);
    setError(null);
    query()
      .then((r) => {
        if (stale) return;
        setList(r.messages);
        setNext(r.next);
      })
      .catch((e: unknown) => !stale && setError(message(e)));
    return () => {
      stale = true;
    };
  }, [query, attempt]);
  // After a send, and live: the first page again, in place, with older pages opened kept below it.
  const listRef = useRef(list);
  listRef.current = list;
  const lastTick = useRef(tick);
  useEffect(() => {
    if (tick === lastTick.current) return;
    lastTick.current = tick;
    let stale = false;
    query()
      .then((r) => {
        if (stale) return;
        const merged = mergeFirstPage(listRef.current, r.messages);
        setList(merged);
        if (merged.length === r.messages.length) setNext(r.next);
      })
      .catch(() => undefined); // what's shown stays; the next event tries again
    return () => {
      stale = true;
    };
  }, [query, tick]);
  // The stream: a message queued, sent, failed or received, or replies read (debounced: a batch is one read).
  useEffect(() => {
    const es = liveStream();
    let timer: ReturnType<typeof setTimeout> | undefined;
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (!MESSAGE_EVENTS.test(type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => setTick((t) => t + 1), 250);
    });
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, []);
  useEffect(() => {
    // the counts on "Replies" (new ones) and "Couldn't send", whatever is shown: the server's own
    // count of all of them, not of a page
    const count = (input: Record<string, unknown>, set: (n: number | null) => void) =>
      client
        .call<{ total: number }>('messages.list', {
          ...input,
          limit: 1,
          ...(who ? { customerId: who.id } : {}),
          ...(job ? { jobId: job.id } : {}),
        })
        .then((r) => set(r.total))
        .catch(() => set(null));
    // a failed one already sent again is dealt with (unsent leaves it out)
    void count({ unsent: true }, setFailedCount);
    void count({ direction: 'in', unread: true }, setUnreadCount);
    // held while the company mirrors ServiceTitan: its segment shows only when there are some
    void count({ status: ['held_mirror'] }, setHeldCount);
  }, [query, who, job, tick]);

  const more = () =>
    next &&
    query(next)
      .then((r) => {
        if (current.current !== query) return; // the filter changed while it loaded
        setList((l) => [...(l ?? []), ...r.messages]);
        setNext(r.next);
      })
      .catch((e: unknown) => setError(message(e)));

  const open = list?.find((m) => m.id === openId) ?? (extra?.id === openId ? extra : null);
  /** "See the copy": from the list when it's there, else messages.get. */
  const openMessage = (id: string) => {
    setWriting(null);
    setAdding(null);
    setAssigning(null);
    if (list?.some((m) => m.id === id)) return setOpenId(id);
    void client
      .call<Message>('messages.get', { id })
      .then((m) => {
        setExtra(m);
        setOpenId(m.id);
      })
      .catch((e: unknown) => setError(message(e)));
  };
  /** A reply opened is read; "Mark as unread" puts it back. */
  const markRead = (m: Message, read: boolean) =>
    client
      .call('messages.markRead', { messageIds: [m.id], read })
      .then(() => {
        const at = read ? new Date().toISOString() : null;
        setList((l) => l && l.map((x) => (x.id === m.id ? { ...x, readAt: at } : x)));
        setUnreadCount((n) => (n === null ? n : Math.max(0, n + (read ? -1 : 1))));
      })
      // it stays new, and opening it again tries again
      .catch(() => undefined);
  const back = () => {
    setOpenId(null);
    setWriting(null);
    setAdding(null);
    setAssigning(null);
  };
  const done = (what: string) => {
    back();
    setSaid(what);
    setTick((t) => t + 1);
  };
  const onRow = (m: Message) => {
    back();
    setOpenId(m.id);
    if (isUnread(m)) void markRead(m, true);
  };

  const place = usePageCrumb(nav);
  const tabs = talkTabs(nav);
  const head = { ...(place ? { crumb: place } : {}), title: 'Texts and email', ...(tabs ? { tabs } : {}) };
  // what's open is its own page, titled with it; the crumb goes back to the messages
  const crumbTo = (title: string): Crumb[] => [
    ...(place?.length ? [place[0]!] : []),
    { label: 'Texts and email', href: '#/messages', onPress: back },
    { label: title },
  ];
  const itsPage = (title: string, list: ListRegion, after?: ReactNode) => (
    <ListPage head={{ crumb: crumbTo(title), title }} list={{ ...list, ...(after ? { after } : {}) }} />
  );

  let body: ReactNode;
  if (writing === 'pick')
    body = (
      <PickCustomer
        page={itsPage}
        onPick={(c) => setWriting({ customerId: c.id })}
        onCancel={() => setWriting(null)}
      />
    );
  else if (writing)
    body = (
      <Writing
        page={itsPage}
        customerId={writing.customerId}
        {...(writing.to ? { to: writing.to } : {})}
        onSent={done}
        onCancel={() => setWriting(null)}
      />
    );
  else if (assigning)
    body = (
      <AssignReply
        page={itsPage}
        m={assigning}
        mirroring={mirroring}
        onDone={done}
        onCancel={() => setAssigning(null)}
      />
    );
  else if (adding)
    body = itsPage('New customer', {
      title: 'Their details',
      children: (
        <div className="msgs__form">
          <p className="msgs__muted">From the number that texted: {theirAddress(adding)}.</p>
          {/* a new customer is ServiceTitan's while mirroring, as from Book and Calls (BL-088) */}
          {mirroring ? (
            <p className="msgs__muted">{MIRROR_LINE}</p>
          ) : (
            <AddCustomer
              m={adding}
              onDone={() => done('Added as a customer. This text and their next ones show under them.')}
              onCancel={() => setAdding(null)}
            />
          )}
        </div>
      ),
    });
  else if (open)
    body = (
      <Detail
        key={open.id}
        page={itsPage}
        m={open}
        timeZone={timeZone}
        canSend={canSend}
        mirroring={mirroring}
        onOnly={(c) => {
          setWho(c);
          setOpenId(null);
        }}
        onReply={() =>
          open.customerId &&
          setWriting({ customerId: open.customerId, to: (isReply(open) && open.from) || open.to })
        }
        onMarkUnread={() => void markRead(open, false)}
        onSeeCopy={() => open.retriedBy && openMessage(open.retriedBy)}
        onAssign={() => setAssigning(open)}
        onAddCustomer={() => setAdding(open)}
        texting={texting}
        onRetried={done}
      />
    );
  else {
    const counts = { failed: failedCount, replies: unreadCount };
    body = (
      <ListPage
        head={head}
        {...(said || (error && list)
          ? {
              notice: (
                <>
                  {said && (
                    <p className="msgs__said" role="status">
                      {said}
                    </p>
                  )}
                  {error && list && (
                    <p className="msgs__bad" role="alert">
                      {error}
                    </p>
                  )}
                </>
              ),
            }
          : {})}
        filters={
          <div className="msgs__filters">
            <Segmented
              label="Show"
              wrap
              items={visibleFilters(heldCount, status).map((f) => ({
                id: f.key,
                label: f.label,
                ...(f.key === 'failed' && counts.failed
                  ? {
                      count: counts.failed,
                      countTone: 'bad' as const,
                      countLabel: `${counts.failed} couldn’t be sent`,
                    }
                  : f.key === 'replies' && counts.replies
                    ? { count: counts.replies, countLabel: `${counts.replies} new` }
                    : {}),
              }))}
              selectedKey={status}
              onSelectionChange={(k) => setStatus(k as StatusFilter)}
            />
            <ListPick
              label="Channel"
              value={channel}
              options={CHANNELS}
              onChange={(v) => setChannel(v as typeof channel)}
            />
            {who && (
              // one name: a hidden span beside the label read with a space before its comma (QA-549)
              <Link label={`Only ${who.name}, show everyone`} onPress={() => setWho(null)}>
                Only {who.name} <span aria-hidden="true">×</span>
              </Link>
            )}
            {job && (
              <Link label={`Only job #${job.number}, show every job`} onPress={() => setJob(null)}>
                Only job #{job.number} <span aria-hidden="true">×</span>
              </Link>
            )}
            {canSend && !mirroring && (
              <span className="msgs__new">
                <Link onPress={() => setWriting('pick')}>New message</Link>
              </span>
            )}
            {canSend && mirroring && <p className="msgs__muted">{MIRROR_LINE}</p>}
          </div>
        }
        list={{
          title: 'Messages',
          what: 'the messages',
          ...(list ? { count: list.length, more: !!next } : {}),
          status: list ? (list.length ? 'ready' : 'empty') : error ? 'failed' : 'loading',
          failedText: error ?? 'The messages didn’t load.',
          onRetry: () => setAttempt((n) => n + 1),
          emptyText:
            status === 'failed'
              ? 'Nothing failed: every message went out.'
              : status === 'replies'
                ? 'No replies yet. When a customer texts back, it shows here.'
                : job
                  ? `No texts or emails on job #${job.number} yet.`
                  : who
                    ? `No messages to ${who.name} yet.`
                    : 'No messages yet. Texts and emails to customers show here as they go.',
          children: list && (
            <RowList
              label="Messages"
              rows={list.map((m) => messageRow(m, timeZone, onRow))}
              {...(next ? { onMore: () => void more() } : {})}
            />
          ),
        }}
      />
    );
  }

  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        {body}
      </main>
    </div>
  );
}

/** Draws an open thing as its own page: its title, its first part, the parts after it. */
type ItsPage = (title: string, list: ListRegion, after?: ReactNode) => ReactNode;

/** One message: where it went, what it said, what happened to it, and what can be done about it. */
function Detail({
  page,
  m,
  timeZone,
  canSend,
  mirroring,
  texting,
  onOnly,
  onReply,
  onMarkUnread,
  onSeeCopy,
  onAssign,
  onAddCustomer,
  onRetried,
}: {
  page: ItsPage;
  m: Message;
  timeZone: string;
  canSend: boolean;
  /** While mirroring: no sending again, writing back or adding a customer; saying whose it is stays. */
  mirroring: boolean;
  /** messages.channels; null when not known. */
  texting: { ok: boolean; notSetUp: string } | null;
  onOnly: (c: { id: string; name: string }) => void;
  onReply: () => void;
  onMarkUnread: () => void;
  /** A failed one sent again: open the copy that went in its place. */
  onSeeCopy: () => void;
  /** A reply from a number on no customer: say which customer it is from. */
  onAssign: () => void;
  /** Or: the new-customer form with that number. */
  onAddCustomer: () => void;
  onRetried: (what: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chip = statusChip(m);
  const retried = !!m.retriedBy;
  const refused = m.errorCode === 'do_not_contact' || m.errorCode === 'consent_missing';
  const cantText = m.channel === 'sms' && texting !== null && !texting.ok;
  const act = async (call: () => Promise<unknown>, what: string) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      onRetried(what);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const kind = m.channel === 'sms' ? 'text' : 'email';
  const them = m.customerName ?? theirAddress(m);
  const retry = () =>
    act(
      () => client.call('messages.retry', { id: m.id }),
      `Sent again: the ${kind} to ${them} is on its way.`,
    );
  // held while the company mirrored ServiceTitan: a person sends it once the company is live (§3.5)
  const release = () =>
    act(
      () => client.call('messages.release', { ids: [m.id] }),
      `Released: the ${kind} to ${them} is on its way.`,
    );
  const reply = isReply(m);
  const steps: KeyValueItem[] = reply
    ? [
        { label: 'Received', value: momentText(messageTime(m), timeZone) },
        ...(m.readAt ? [{ label: 'Opened in the office', value: momentText(m.readAt, timeZone) }] : []),
      ]
    : [
        { label: 'Written', value: momentText(m.queuedAt, timeZone) },
        ...(m.sentAt ? [{ label: 'Handed to the provider', value: momentText(m.sentAt, timeZone) }] : []),
        ...(m.deliveredAt ? [{ label: 'Delivered', value: momentText(m.deliveredAt, timeZone) }] : []),
      ];
  if (isFailed(m))
    steps.push({
      label: m.status === 'undelivered' ? 'Not delivered' : 'Couldn’t send',
      value: errorWords(m),
      bad: true,
    });
  if (retried) steps.push({ label: 'Sent again', value: 'A copy went in its place.', tone: 'muted' });
  if (m.status === 'queued' || m.status === 'sending')
    steps.push({ label: 'Waiting to go', value: 'It goes out in a moment.', tone: 'muted' });
  const sendAgain = canSend && !mirroring && isFailed(m) && !retried && !refused;
  return page(
    them,
    {
      title: 'The message',
      children: (
        <div className="msgs__message">
          <p className="msgs__state">
            <Label kind={labelKind(chip.tone)}>{chip.text}</Label>
          </p>
          <p className="msgs__muted">
            {channelLine(m)}
            {reply
              ? m.customerId
                ? ''
                : ', not on any customer yet'
              : `, ${purposeWords(m.purpose).toLowerCase()}`}
            {m.retryOf ? ', sent again in place of one that couldn’t be sent' : ''}
          </p>
          {m.subject && <p className="msgs__subject">{m.subject}</p>}
          <p className="msgs__body" data-empty={m.body.trim() ? undefined : ''}>
            {bodyText(m)}
          </p>
          {reply && m.purpose === 'stop' && (
            <p className="msgs__muted">They texted STOP: no more texts to this number.</p>
          )}
          {reply && m.purpose === 'start' && (
            <p className="msgs__muted">They texted START: texts to this number are back on.</p>
          )}
        </div>
      ),
    },
    <>
      <Section title="What happened">
        <KeyValue items={steps} layout="spread" label="What happened" />
      </Section>
      <div className="msgs__form">
        {error && (
          <p className="msgs__bad" role="alert">
            {error}
          </p>
        )}
        {sendAgain && cantText && <p className="msgs__muted">{texting!.notSetUp}</p>}
        <div className="msgs__acts">
          {sendAgain && !cantText && (
            <Button variant="primary" isBusy={busy} onPress={() => void retry()}>
              Send it again
            </Button>
          )}
          {canSend && m.status === 'held_mirror' && (
            <Button variant="primary" isBusy={busy} onPress={() => void release()}>
              Send it now
            </Button>
          )}
          {/* after STOP they can't be texted here, so no "Write back" */}
          {canSend && !mirroring && m.customerId && !(reply && m.purpose === 'stop') && (
            <Link onPress={onReply}>{reply ? 'Write back' : `Write to ${m.customerName ?? 'them'}`}</Link>
          )}
          {retried && <Link onPress={onSeeCopy}>See the copy</Link>}
          {canSend && reply && !m.customerId && m.from && (
            <>
              <Link onPress={onAssign}>Say whose it is</Link>
              {!mirroring && <Link onPress={onAddCustomer}>Add as a customer</Link>}
            </>
          )}
          {reply && m.readAt && <Link onPress={onMarkUnread}>Mark as unread</Link>}
          {m.customerId && m.customerName && (
            <Link onPress={() => onOnly({ id: m.customerId!, name: m.customerName! })}>
              All messages with {m.customerName}
            </Link>
          )}
        </div>
      </div>
    </>,
  );
}

/** New message to a customer: their phones and emails, then the composer (SendMessage). */
function Writing({
  page,
  customerId,
  to,
  onSent,
  onCancel,
}: {
  page: ItsPage;
  customerId: string;
  to?: string;
  onSent: (what: string) => void;
  onCancel: () => void;
}) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  return page('New message', {
    title: draftTitle(customer),
    children: (
      <SendMessage
        customerId={customerId}
        {...(to ? { to } : {})}
        onSent={onSent}
        onCancel={onCancel}
        onCustomer={setCustomer}
      />
    ),
  });
}

/** Add as a customer: the new-customer form (use-new-customer.ts) on NewCustomerFields, with the number. */
function AddCustomer({ m, onDone, onCancel }: { m: Message; onDone: () => void; onCancel: () => void }) {
  const nc = useNewCustomer({
    start: formFromSearch(theirAddress(m)),
    who: 'this number',
    // the new customer has the number, so this text goes under them too
    link: (customerId) => client.call('messages.assignCustomer', { messageId: m.id, customerId }),
    onDone,
  });
  return (
    <NewCustomerFields
      values={nc.form}
      onChange={nc.change}
      onCheck={nc.check}
      consentNote="Consent (saved with who and when)"
      submitLabel="Add customer"
      busyLabel="Adding the customer…"
      isBusy={nc.busy}
      isSubmitDisabled={nc.blocked}
      error={nc.error}
      onSubmit={() => nc.create()}
      onCancel={onCancel}
      dupe={
        nc.dupe
          ? {
              text: nc.dupe.text,
              pickLabel: nc.dupe.pickLabel,
              onPick: nc.dupe.pick,
              isHeld: nc.held !== null,
              onAddAnyway: () => nc.create(true),
            }
          : null
      }
      unchecked={nc.uncheckedText ? { text: nc.uncheckedText, onAddAnyway: () => nc.create(true) } : null}
    />
  );
}

type Found = { id: string; displayName: string; city: string | null };

/** Find a customer (customers.list): for "New message", then write to them; or whose a reply is. */
function PickCustomer({
  page,
  onPick,
  onCancel,
  title = 'New message',
  label = 'Who to',
  lede,
  error,
}: {
  page: ItsPage;
  onPick: (c: { id: string; displayName: string }) => void;
  onCancel: () => void;
  title?: string;
  label?: string;
  lede?: string;
  error?: string | null;
}) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Found[] | null>(null);
  useEffect(() => {
    if (q.trim().length < 2) return setFound(null);
    const t = setTimeout(() => {
      client
        .call<{ customers: Found[] }>('customers.list', { query: q.trim(), limit: 8 })
        .then((r) => setFound(r.customers))
        .catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return page(title, {
    title: 'Find the customer',
    children: (
      <div className="msgs__form">
        {lede && <p className="msgs__muted">{lede}</p>}
        <TextField label={label} value={q} onChange={setQ} placeholder="A name, phone or address" autoFocus />
        {error && (
          <p className="msgs__bad" role="alert">
            {error}
          </p>
        )}
        {found && (
          <RowList
            label="Customers found"
            rows={found.map((c) => ({
              id: c.id,
              name: c.displayName,
              onOpen: () => onPick(c),
              ...(c.city ? { lines: [{ text: c.city }] } : {}),
            }))}
            empty={`No customer matches “${q.trim()}”.`}
          />
        )}
        <div className="msgs__acts">
          <Link onPress={onCancel}>Cancel</Link>
        </div>
      </div>
    ),
  });
}

/**
 * "Say whose it is": a reply from a number on no customer goes under the customer it is from
 * (messages.assignCustomer). The number can be kept on one of their contacts, or a new one
 * (messages.assignNewContact), so their
 * next texts match; that gives no consent to text them. A number already another customer's is
 * refused in the api's words.
 */
function AssignReply({
  page,
  m,
  mirroring,
  onDone,
  onCancel,
}: {
  page: ItsPage;
  m: Message;
  /** While mirroring the text is attached, but its number isn't kept: contacts are ServiceTitan's. */
  mirroring: boolean;
  onDone: (what: string) => void;
  onCancel: () => void;
}) {
  const [picked, setPicked] = useState<Customer | null>(null);
  const [keep, setKeep] = useState<string>('none');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const number = theirAddress(m);
  const title = 'Whose text is this?';
  if (!picked)
    return (
      <PickCustomer
        page={page}
        title={title}
        label="Customer"
        lede={`From ${number}: “${firstLine(bodyText(m), 60)}”`}
        error={error}
        onPick={(c) =>
          void client
            .call<Customer>('customers.get', { id: c.id })
            .then((full) => {
              setPicked(full);
              // their one contact, when there is just one, else a choice
              setKeep(!mirroring && full.contacts.length === 1 ? full.contacts[0]!.id : 'none');
            })
            .catch((e: unknown) => setError(message(e)))
        }
        onCancel={onCancel}
      />
    );
  const missing = keep === 'new' && !newName.trim() ? 'Add the new contact’s name.' : null;
  const save = async () => {
    if (missing) return;
    setBusy(true);
    setError(null);
    try {
      // keeping the number writes the customer's contacts, refused while mirroring; attaching alone isn't
      if (keep === 'none')
        await client.call('messages.assignCustomer', { messageId: m.id, customerId: picked.id });
      else
        await client.call('messages.assignNewContact', {
          messageId: m.id,
          customerId: picked.id,
          addNumber: keep === 'new' ? { newContactName: newName.trim() } : { contactId: keep },
        });
      onDone(
        keep === 'none'
          ? `This text is under ${picked.displayName} now.`
          : `This text is under ${picked.displayName} now, and their next texts from ${number} will be too.`,
      );
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return page(title, {
    title: `This is ${picked.displayName}`,
    children: (
      <div className="msgs__form">
        {mirroring ? (
          <p className="msgs__muted">
            Keeping {number} on their page: {MIRROR_LINE}
          </p>
        ) : (
          <>
            <ChoiceGroup
              label={`Keep ${number} on their page?`}
              selectionMode="single"
              layout="stack"
              value={keep}
              onChange={setKeep}
            >
              {picked.contacts.map((k) => (
                <Choice key={k.id} value={k.id}>
                  On {k.name}
                </Choice>
              ))}
              <Choice value="new">On a new contact</Choice>
              <Choice value="none">No, just this text</Choice>
            </ChoiceGroup>
            {keep === 'new' && (
              <TextField label="Their name" value={newName} maxLength={200} onChange={setNewName} />
            )}
          </>
        )}
        {keep !== 'none' && (
          <p className="msgs__muted">Keeping the number doesn’t give consent: ask them before texting it.</p>
        )}
        {error && (
          <p className="msgs__bad" role="alert">
            {error}
          </p>
        )}
        <div className="msgs__acts">
          <Button variant="primary" isBusy={busy} isDisabled={!busy && !!missing} onPress={() => void save()}>
            {`This is ${picked.displayName}`}
          </Button>
          <Link onPress={() => setPicked(null)}>Someone else</Link>
          <Link onPress={onCancel}>Cancel</Link>
        </div>
        <p className="msgs__muted" role="status" aria-live="polite">
          {missing && !busy ? missing : ''}
        </p>
      </div>
    ),
  });
}
